import { Star, StarBorderOutlined } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { addFavourite, removeFavourite } from '../../api/mobileapi';
import { AccessPointData } from '../../api/types';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { Severity, useToast } from '../ToastProvider/ToastProvider';

interface AccessPointDetailsProps {
    accessInformation: AccessPointData;
    translations: Record<string, string>;
}

export const AccessPointDetails: React.FC<AccessPointDetailsProps> = ({ accessInformation, translations }) => {
    const { currentLanguage } = useHomeData();
    const [fav, setFav] = useState<boolean>(accessInformation.isFavourite);
    const toast = useToast();

    const handleError = () => {
        toast(translations.ERROR_OPERATION_FAILED, Severity.error);
        setFav((prev) => !prev);
    };

    const { mutate: addFavMutate } = useMutation(addFavourite, {
        onSuccess: () => {
            toast(translations.MY_FAVOURITES_ADD, Severity.success);
            setFav(true);
        },
        onError: handleError,
    });

    const { mutate: removeFavMutate } = useMutation(removeFavourite, {
        onSuccess: () => {
            toast(translations.MY_FAVOURITES_REMOVE, Severity.success);
            setFav(false);
        },
        onError: handleError,
    });

    const handleClick = () =>
        fav
            ? removeFavMutate({ language: currentLanguage, accessPointCode: accessInformation.accessPointCode })
            : addFavMutate({ language: currentLanguage, accessPointCode: accessInformation.accessPointCode });

    return (
        <Box
            onClick={handleClick}
            aria-label="favouritesClick"
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: 'fit-content',
                padding: '0.25rem 1.5rem',
                margin: '0 auto',
                color: 'white',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconButton sx={{ color: 'white' }} size="small" edge={'start'}>
                    {fav ? <Star /> : <StarBorderOutlined />}
                </IconButton>
                <Typography variant="h6" fontWeight={600} textAlign={'center'}>
                    {accessInformation.accessPointCode}
                </Typography>
            </Box>
            <Typography textAlign={'center'} variant={'subtitle1'}>
                {accessInformation.accessPointDescription}
            </Typography>
        </Box>
    );
};
